import { Badge } from "@/components/ui/badge";
import { ConfirmActionDialog } from "@/components/ui/confirm-action-dialog";
import { useToast } from "@/components/ui/use-toast";
import { useDeleteMemory, useEditMemory } from "@/lib/api/memories";
import { formatDistanceToNow } from "date-fns";
import { Check, Pencil, Trash2, X } from "lucide-react";
import { useState } from "react";
import TextareaAutosize from "react-textarea-autosize";

interface TeenMemoryItemProps {
  memory: {
    id: string;
    memory: string;
    type: string;
    updated_at: string;
  };
  userId: string;
}

export function TeenMemoryItem({ memory, userId }: TeenMemoryItemProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(memory.memory);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const { toast } = useToast();

  const editMutation = useEditMemory(userId);
  const deleteMutation = useDeleteMemory(userId);

  const handleEdit = () => setIsEditing(true);
  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditValue(memory.memory);
  };

  const handleSaveEdit = () => {
    if (editValue.trim() === "") {
      return;
    }

    if (editValue.trim() === memory.memory) {
      setIsEditing(false);
      return;
    }

    editMutation.mutate(
      {
        userId,
        memoryId: memory.id,
        memory: editValue,
        type: memory.type as "short_term" | "long_term",
      },
      {
        onSuccess: () => {},
        onError: () => {
          setIsEditing(true);
          toast({
            title: "Error updating memory",
            description: "Please try again",
          });
        },
      },
    );
    setIsEditing(false);
  };

  const handleDelete = () => {
    deleteMutation.mutate(
      { userId, memoryId: memory.id },
      {
        onSuccess: () => setShowDeleteDialog(false),
      },
    );
  };

  const isLoading = editMutation.isPending || deleteMutation.isPending;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm leading-6 opacity-100 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1">
          {isEditing ? (
            <TextareaAutosize
              className="w-full resize-none border-0 bg-transparent text-sm leading-6 text-black outline-none"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              rows={2}
              autoFocus
              style={{ fontFamily: "inherit" }}
              disabled={isLoading}
            />
          ) : (
            memory.memory
          )}
        </div>
        <div className="flex items-center gap-2">
          <Badge
            variant={memory.type === "long_term" ? "secondary" : "default"}
          >
            {memory.type === "long_term" ? "Long Term" : "Short Term"}
          </Badge>
          {isEditing ? (
            <>
              <button
                className="ml-2 text-green-600 hover:text-green-800"
                onClick={handleSaveEdit}
                title="Save"
                disabled={isLoading || editValue.trim() === ""}
              >
                <Check size={18} />
              </button>
              <button
                className="text-gray-400 hover:text-gray-600"
                onClick={handleCancelEdit}
                title="Cancel"
                disabled={isLoading}
              >
                <X size={18} />
              </button>
            </>
          ) : (
            <>
              <button
                className="ml-2 text-gray-400 hover:text-blue-600"
                onClick={handleEdit}
                title="Edit"
                disabled={isLoading}
              >
                <Pencil size={18} />
              </button>
              <button
                className="text-gray-400 hover:text-red-600"
                onClick={() => setShowDeleteDialog(true)}
                title="Delete"
                disabled={isLoading}
              >
                <Trash2 size={18} />
              </button>
            </>
          )}
        </div>
      </div>
      <div className="mt-2 text-xs text-gray-500">
        {formatDistanceToNow(new Date(memory.updated_at), {
          addSuffix: true,
        })}
      </div>
      <ConfirmActionDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDelete}
        itemName="delete this memory"
        title="Delete Memory"
        description="Are you sure you want to delete this memory? This action cannot be undone."
        loading={deleteMutation.isPending}
      />
    </div>
  );
}
